import { useState, type ReactNode } from "react";
import { SlidersHorizontal, ChevronDown } from "lucide-react";
import { buildBundleSubPills, buildPrimaryPills, BUNDLE_PILL_ID } from "@/store/categoryFilter";

interface CategoryFilterProps {
  activeCategories: string[];
  bundleFocus: string[];
  onToggleCategory: (id: string) => void;
  onToggleBundleFocus: (id: string) => void;
  onClearAll: () => void;
  purchasedOnly: boolean;
  onTogglePurchasedOnly: () => void;
  showPurchasedPill: boolean;
  showClaimBundlePill: boolean;
  claimBundleBusy: boolean;
  onClaimBundleCoupons: () => void;
}

function Pill({
  active,
  onClick,
  disabled,
  children,
}: {
  active: boolean;
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={
        "agd:cursor-pointer agd:whitespace-nowrap agd:rounded-full agd:px-4.5 agd:py-2.5 agd:text-[0.85rem] agd:font-bold agd:shrink-0 agd:max-md:px-3.5 agd:max-md:py-2 agd:max-md:text-[12px] agd:disabled:cursor-not-allowed agd:disabled:opacity-60 " +
        (active
          ? "agd:bg-primary agd:text-white agd:shadow-[0_4px_12px_rgba(0,162,229,.25)]"
          : "agd:bg-card agd:text-[#75767a] agd:shadow-[0_1px_3px_rgba(0,0,0,.06)]")
      }
    >
      {active ? "✓ " : ""}
      {children}
    </button>
  );
}

export default function CategoryFilter({
  activeCategories,
  bundleFocus,
  onToggleCategory,
  onToggleBundleFocus,
  onClearAll,
  purchasedOnly,
  onTogglePurchasedOnly,
  showPurchasedPill,
  showClaimBundlePill,
  claimBundleBusy,
  onClaimBundleCoupons,
}: CategoryFilterProps) {
  const primaryPills = buildPrimaryPills();
  const showBundleFocus = activeCategories.includes(BUNDLE_PILL_ID);
  const [mobileOpen, setMobileOpen] = useState(false);
  const activeCount = activeCategories.length + (purchasedOnly ? 1 : 0);

  return (
    <div className="agd:mb-6 agd:max-md:mb-4 agd:flex agd:flex-col agd:gap-3">
      <button
        type="button"
        onClick={() => setMobileOpen((open) => !open)}
        className="agd:hidden agd:max-md:flex agd:w-full agd:items-center agd:justify-between agd:rounded-lg agd:border agd:border-[#cbd5e0] agd:bg-white agd:px-4 agd:py-2.5 agd:text-[0.85rem] agd:font-semibold agd:text-foreground agd:shadow-sm"
      >
        <span className="agd:flex agd:items-center agd:gap-2">
          <SlidersHorizontal size={15} className="agd:text-primary" />
          Filter{activeCount > 0 ? ` (${activeCount})` : ""}
        </span>
        <ChevronDown
          size={16}
          className={"agd:text-muted-foreground agd:transition-transform" + (mobileOpen ? " agd:rotate-180" : "")}
        />
      </button>

      <div
        className={
          "agd:flex agd:flex-wrap agd:gap-2.5 " +
          (mobileOpen
            ? "agd:max-md:rounded-lg agd:max-md:border agd:max-md:border-[#e1e3e5] agd:max-md:bg-white agd:max-md:p-3 agd:max-md:shadow-sm"
            : "agd:max-md:hidden")
        }
      >
        {primaryPills.map((pill) => {
          const isActive = pill.id === "ALL" ? activeCategories.length === 0 : activeCategories.includes(pill.id);
          return (
            <Pill key={pill.id} active={isActive} onClick={() => (pill.id === "ALL" ? onClearAll() : onToggleCategory(pill.id))}>
              {pill.label}
            </Pill>
          );
        })}
        {showPurchasedPill ? (
          <Pill active={purchasedOnly} onClick={onTogglePurchasedOnly}>
            My Purchases
          </Pill>
        ) : null}
        {showClaimBundlePill ? (
          <Pill active={false} disabled={claimBundleBusy} onClick={onClaimBundleCoupons}>
            {claimBundleBusy ? "Claiming…" : "Claim Bundle Coupon(s)"}
          </Pill>
        ) : null}
      </div>
      {showBundleFocus ? (
        <div className="agd:flex agd:flex-wrap agd:items-center agd:gap-2 agd:rounded-[10px] agd:border agd:border-[#fce3c3] agd:bg-[#fff7ee] agd:px-4 agd:py-3">
          <span className="agd:text-[0.8rem] agd:font-bold agd:text-[#c0630a]">Bundle includes:</span>
          <div className="agd:flex agd:flex-wrap agd:gap-2">
            {buildBundleSubPills().map((pill) => (
              <Pill key={pill.id} active={bundleFocus.includes(pill.id)} onClick={() => onToggleBundleFocus(pill.id)}>
                {pill.label}
              </Pill>
            ))}
          </div>
          <span className="agd:text-[0.75rem] agd:text-muted-foreground">
            (leave unselected to show all bundle options)
          </span>
        </div>
      ) : null}
    </div>
  );
}
